import { SxProps } from '@mui/system';
import {
  MultimediaManager2,
  NotificationTypeEnum,
  OneMedia,
  isImage,
  notificationQueue,
  useNotification,
} from '@prismamedia/one-components';
import { BrandKey } from '__generated__/queries-web';
import { move } from 'ramda';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useUploadImage } from 'utils/useUploadImage';
import { MediaFields } from './MediaFields';
import { MediaUploaders } from './MediaUploaders';

export const MediaInput: FC<{
  medias: OneMedia[];
  setMedias: Dispatch<SetStateAction<OneMedia[]>>;
  brandKey: BrandKey;
  sx?: SxProps;
  withMediaUploaders?: boolean;
}> = ({ medias, setMedias, brandKey, sx, withMediaUploaders }) => {
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const { pushNotification, removeNotification } = useNotification();
  const uploadImage = useUploadImage();

  const selectedMedia = medias[selectedMediaIndex];
  const mediaHasError = (media: OneMedia) =>
    isImage(media) && (!media.credit || !media.source);

  const updateSelectedMedia: Dispatch<SetStateAction<OneMedia>> = (updater) => {
    setMedias((prev) =>
      prev.map((media, i) => {
        const newMedia =
          typeof updater === 'function' ? updater(media) : updater;
        return i === selectedMediaIndex ? newMedia : media;
      }),
    );
  };

  const onUploadFiles = async (files: File[]) => {
    setLoading(true);
    try {
      const queue = files.map((file) => () =>
        uploadImage(file, brandKey.toLowerCase()),
      );

      const newMedias = await notificationQueue(
        queue,
        pushNotification,
        removeNotification,
        'Importation',
      );

      const lastMediaIndex = medias.length - 1;
      setMedias((prev) => [...prev, ...newMedias]);
      setSelectedMediaIndex(lastMediaIndex + 1);
    } catch (error) {
      pushNotification({
        message: (error as Error).message,
        type: NotificationTypeEnum.error,
      });
    }
    setLoading(false);
  };

  return (
    <MultimediaManager2
      sx={sx}
      medias={medias}
      loading={loading}
      onMediaDelete={(index) =>
        setMedias((prev) => prev.filter((_, i) => i !== index))
      }
      onMediaReorder={(oldIndex, newIndex) =>
        setMedias((prev) => move(oldIndex, newIndex, prev))
      }
      onCropAndFocusPointChange={({ crop, focusPoint }, index) =>
        setMedias((prev) =>
          prev.map((media, i) =>
            i === index ? { ...media, focusPoint, crop } : media,
          ),
        )
      }
      mediaWithErrorIndexes={medias.reduce<number[]>(
        (prev, media, i) => (mediaHasError(media) ? [...prev, i] : prev),
        [],
      )}
      selectedMediaIndex={selectedMediaIndex}
      setSelectedMediaIndex={setSelectedMediaIndex}
      onUploadFiles={onUploadFiles}
      mediaUploaders={
        withMediaUploaders && (
          <MediaUploaders
            medias={medias}
            setMedias={setMedias}
            brandKey={brandKey}
            setSelectedMediaIndex={setSelectedMediaIndex}
          />
        )
      }
      mediaFields={
        <MediaFields media={selectedMedia} setMedia={updateSelectedMedia} />
      }
    />
  );
};
